import {getComboMarkdownEditor} from './comp/ComboMarkdownEditor.ts';
import {POST} from '../modules/fetch.ts';

// Wires the DRCY chat compose box (the real Gitea comment editor) and the End
// Chat button. It coordinates with the message list (the <drcy-chat> Vue
// component) only through the server: sending posts a message, the server
// dispatches the turn and publishes SSE events that the message list renders;
// ending cancels the run, clears messages, and publishes a "cleared" event.
export function initDrcyChat(): void {
  let hasArtifactDrafts = false;
  document.addEventListener('drcy-chat-artifacts-dirty', (e: Event) => {
    hasArtifactDrafts = (e as CustomEvent<boolean>).detail;
  });
  const composeArea = document.querySelector<HTMLElement>('.drcy-chat-compose-area');
  if (composeArea) {
    const editorEl = composeArea.querySelector<HTMLElement>('.combo-markdown-editor');
    const sendButton = composeArea.querySelector<HTMLButtonElement>('.drcy-chat-send');
    const postUrl = composeArea.getAttribute('data-post-url');
    const scope = composeArea.getAttribute('data-scope') ?? '';

    const submit = async () => {
      const editor = getComboMarkdownEditor(editorEl);
      if (!editor || !postUrl || sendButton?.disabled) return;
      const content = editor.value().trim();
      if (!content) return;
      sendButton.disabled = true;
      try {
        const res = await POST(postUrl, {data: {content, scope}});
        // On success clear the box and keep the button disabled: the turn is now
        // in flight, and the message list re-enables it via 'drcy-chat-busy' when
        // the turn finishes. On 409 (a turn is still running) or any error, leave
        // the text and re-enable so the user can retry.
        if (res.ok) {
          editor.value('');
          // Tell the message list to reconcile from history immediately rather
          // than waiting for the SSE 'message' events. If the stream is silently
          // dead or served by another Hub process, this is what makes the new
          // turn appear and starts the fallback poll (so Send re-enables).
          document.dispatchEvent(new CustomEvent('drcy-chat-reconcile'));
        } else {
          sendButton.disabled = false;
        }
      } catch (err) {
        console.error('Failed to send DRCY chat message', err);
        sendButton.disabled = false;
      }
    };

    sendButton?.addEventListener('click', submit);

    // The message list (<drcy-chat>) drives the composer's enabled state: send is
    // disabled while a turn is responding and re-enabled once nothing is in
    // flight. This is authoritative over the optimistic disable in submit(), so a
    // reload mid-turn (or a turn started elsewhere) also disables send.
    document.addEventListener('drcy-chat-busy', (e: Event) => {
      if (sendButton) sendButton.disabled = (e as CustomEvent<boolean>).detail;
    });
    // Ctrl/Cmd+Enter sends, matching Gitea's comment-form shortcut.
    editorEl?.addEventListener('keydown', (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
        e.preventDefault();
        submit();
      }
    });

    // The landing-state example prompts (in the <drcy-chat> component) prefill
    // the composer via this event, then focus it so the user can review and send.
    document.addEventListener('drcy-chat-prefill', (e: Event) => {
      const editor = getComboMarkdownEditor(editorEl);
      if (!editor) return;
      editor.value((e as CustomEvent<string>).detail);
      editorEl?.querySelector('textarea')?.focus();
    });
  }

  // The repo-level scope picker is a fomantic dropdown of anchor links
  // (?scope=…), so selecting an option navigates on its own — no JS needed.

  const endButton = document.querySelector<HTMLButtonElement>('.drcy-chat-end');
  endButton?.addEventListener('click', async () => {
    const endUrl = endButton.getAttribute('data-end-url');
    if (!endUrl) return;
    const scope = endButton.getAttribute('data-scope') ?? '';
    if (hasArtifactDrafts && !window.confirm('End this chat and discard all uncommitted artifacts?')) return;
    endButton.disabled = true;
    try {
      const res = await POST(`${endUrl}?scope=${encodeURIComponent(scope)}`);
      // Clear the transcript locally on success instead of relying solely on the
      // SSE 'cleared' event: if the stream is dead or on another Hub process the
      // server still deleted the conversation, and the view must reflect that.
      if (res.ok) {
        document.dispatchEvent(new CustomEvent('drcy-chat-ended'));
      }
    } catch (err) {
      console.error('Failed to end DRCY chat', err);
    } finally {
      endButton.disabled = false;
    }
  });
}
